// SL.L2 (#2958) — a SPIKE's outcome (SL.G1 #2953, ruling 6241349909 item 11),
// reused by L1's issue drawer (Plan v1.4). G1's Definition of Done for a
// planned spike: its issue can't be set DONE until this summary exists.
//
// Shown on SPIKE items only. With `record_outcome` in the item's
// allowedActions it is an editor; without it, the saved outcome as text (W1:
// no role logic here). The caller sends the POST (onSave), as BacklogPage's
// add form does, so a page that allows one write at a time can hold its lock
// across the save and the re-read; recordSpikeOutcome (lib/backlogApi) is the
// call. onSave rejecting keeps the editor open with the server's message.
import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { hasAction } from '../lib/sprintLoopActions';
import type { BacklogItem, SpikeOutcomeBody } from '../lib/backlogApi';

export interface SpikeOutcomeEditorProps {
  item: Pick<BacklogItem, 'key' | 'type' | 'outcomeSummary' | 'outcomeAdrId' | 'allowedActions'>;
  onSave: (body: SpikeOutcomeBody) => Promise<void>;
  /** Another write is in progress: the editor can be filled but not saved. */
  blocked?: boolean;
  /** Open the editor at once, prefilled (SL.L1a-2's SPIKE prompt in the board's drawer). */
  startOpen?: boolean;
}

// G1's outcomeBody: the summary is 1–4000 characters once trimmed; the ADR id a UUID.
const MAX_SUMMARY = 4000;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const textStyle: CSSProperties = { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', margin: '4px 0 0', overflowWrap: 'anywhere' };
const smallButton: CSSProperties = {
  padding: '4px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)',
  background: 'var(--surface)', color: 'var(--text)', fontSize: 12, fontWeight: 500, cursor: 'pointer',
};
const fieldStyle: CSSProperties = {
  width: '100%', fontFamily: 'var(--font-body)', fontSize: 13, padding: '6px 8px',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--surface)', color: 'var(--text)',
};
const labelStyle: CSSProperties = { display: 'block', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 2 };

export default function SpikeOutcomeEditor({ item, onSave, blocked = false, startOpen = false }: SpikeOutcomeEditorProps) {
  const [editing, setEditing] = useState(startOpen);
  // Prefilled for startOpen; open() prefills again each time the editor opens.
  const [summary, setSummary] = useState(item.outcomeSummary ?? '');
  const [adr, setAdr] = useState(item.outcomeAdrId ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Closing the editor (cancel or save) puts focus back on its trigger (R1 P3).
  const triggerRef = useRef<HTMLButtonElement>(null);
  const refocus = useRef(false);
  useEffect(() => {
    if (!editing && refocus.current) {
      refocus.current = false;
      triggerRef.current?.focus();
    }
  }, [editing]);
  const close = () => { refocus.current = true; setEditing(false); };

  if (item.type !== 'SPIKE') return null;
  const saved = item.outcomeSummary?.trim() ? item.outcomeSummary : null;

  if (!hasAction(item.allowedActions, 'record_outcome')) {
    return (
      <p style={{ ...textStyle, color: 'var(--text-secondary)' }}>
        <strong>Outcome:</strong> <span>{saved ?? 'Not recorded yet.'}</span>
        {item.outcomeAdrId && <> (ADR {item.outcomeAdrId})</>}
      </p>
    );
  }

  function open() {
    // Prefill from the last read, every time the editor opens.
    setSummary(item.outcomeSummary ?? '');
    setAdr(item.outcomeAdrId ?? '');
    setError(null);
    setEditing(true);
  }

  if (!editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <p style={textStyle}><strong>Outcome:</strong> <span>{saved ?? 'Not recorded yet.'}</span></p>
        <button type="button" ref={triggerRef} onClick={open} style={smallButton}>{saved ? 'Edit outcome' : 'Record outcome'}</button>
      </div>
    );
  }

  const adrText = adr.trim();
  const adrProblem = adrText !== '' && !UUID.test(adrText) ? 'The ADR id must be a UUID, or left blank.' : null;
  const canSave = !saving && !blocked && summary.trim() !== '' && adrProblem === null;
  const summaryId = `spike-outcome-summary-${item.key}`;
  const adrId = `spike-outcome-adr-${item.key}`;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSave) return;
    const body: SpikeOutcomeBody = { outcomeSummary: summary.trim() };
    // Unchanged: leave the link as it is. Cleared: null clears it (G1).
    if (adrText !== (item.outcomeAdrId ?? '')) body.outcomeAdrId = adrText === '' ? null : adrText;
    setSaving(true);
    setError(null);
    try {
      await onSave(body);
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} aria-label={`Outcome of ${item.key}`} style={{ display: 'grid', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
      <div>
        <label htmlFor={summaryId} style={labelStyle}>Outcome summary for {item.key}</label>
        <textarea
          id={summaryId} rows={3} maxLength={MAX_SUMMARY} autoFocus
          value={summary} onChange={(e) => setSummary(e.target.value)} style={fieldStyle}
        />
      </div>
      <div>
        <label htmlFor={adrId} style={labelStyle}>ADR id for {item.key} (optional)</label>
        <input
          id={adrId} value={adr} onChange={(e) => setAdr(e.target.value)} style={fieldStyle}
          aria-describedby={adrProblem ? `${adrId}-problem` : undefined}
        />
        {adrProblem && (
          <p id={`${adrId}-problem`} style={{ ...textStyle, fontSize: 12, color: 'var(--error)' }}>{adrProblem}</p>
        )}
      </div>
      {error && <p role="alert" style={{ ...textStyle, color: 'var(--error)', margin: 0 }}>{error}</p>}
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <button type="submit" disabled={!canSave} style={{ ...smallButton, cursor: canSave ? 'pointer' : 'not-allowed', opacity: canSave ? 1 : 0.6 }}>
          {saving ? 'Saving…' : 'Save outcome'}
        </button>
        <button type="button" onClick={close} disabled={saving} style={smallButton}>Cancel</button>
      </div>
    </form>
  );
}
